import React, {useEffect, useState} from 'react';
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogHeader from "../dialogHeader";
import {Box, CircularProgress, FormControl, Grid, TextField, useMediaQuery, useTheme} from "@mui/material";
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Typography from "@mui/material/Typography";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import dayjs, {Dayjs} from "dayjs";
import styles from './scoreCardDialog.module.css';
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {NumericFormat} from 'react-number-format';
import Button from "@mui/material/Button";
import {axiosRequestData} from "../../../helpers/axios";
import {recordScore} from "../../../interfaces/recordScore";
import DateIcon from "../../icons/dateIcon";
import {scorecardAddEvent} from '../../mixPanelEvents/mixPanelEvents';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import {
    UseiGolfData,
    UseiGolfCourseTeeDetails,
    UseiGolfGetStates
} from '../../../hooks/useiGolfData';
import IgolfTextfield from './igolfTextfield/igolfTextfield';
import {iGolfCourseTee} from "../../../interfaces/iGolfCourseTee";
import {iGolfCourse} from "../../../interfaces/iGolfCourse";
import { extractActivePropertyLabels } from '../../../helpers/utils';
import ScoreCardForm from './scoreCardForm';

const ScoreCardDialog = ({show, setShow, record, callback}: {
    show: boolean,
    setShow: (value: boolean) => void,
    record?: recordScore,
    callback?: (response: any, scoreboard: recordScore) => void,
}) => {
    const [dateValue, setDateValue] = useState<Dayjs | null>(dayjs(new Date()));
    const [score, setScore] = useState("");
    const [courseName, setCourseName] = useState("");
    const [fairway, setFairway] = useState("");
    const [greensInRegulation, setGreensInRegulation] = useState("");
    const [numberOfPutts, setNumberOfPutts] = useState("");
    const [upAndDowns, setUpAndDowns] = useState("");
    const [chippings, setChippings] = useState("");
    const [mental, setMental] = useState("");
    const [strokes, setStrokes] = useState("");
    const [holes, setHoles] = useState("");
    const [expanded, setExpanded] = useState(false);
    const [recordID, setRecordID] = useState(0);
    const [loading, setLoading] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [numHoles, setNumHoles] = useState<string>('18');
    const { courseList, setCourseList, searchiGolfCourses, setSelectedCourse, selectedCourse, loadingiGolf } = UseiGolfData();
    const { teeDetails, setTeeDetails, getTeeDetails, loadingTee, selectedTee, setSelectedTee, getTeeDetailsText, gender, setGender } = UseiGolfCourseTeeDetails();
    const { countryStateList, getStateListByCountries } = UseiGolfGetStates();
    const [iGolfTeeText, setiGolfTeeText] = useState<string>('');
    const debounceTimeout = React.useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (record) {
            setDateValue(dayjs(new Date(record.record_date)));
            setScore(record.score.toString());
            setCourseName(record.course);
            setFairway(record.driving);
            setGreensInRegulation(record.ball_striking);
            setNumberOfPutts(record.putting);
            setUpAndDowns(record.chipping_bunkers_1);
            setChippings(record.chipping_bunkers_2);
            setMental(record.mental_game);
            setStrokes(record.wedge_game_1);
            setHoles(record.wedge_game_2);
            setRecordID(record.record_id ? record.record_id : 0);
            setSelectedCourse(record.courseObj as iGolfCourse);
            setNumHoles(record.numHoles ? record.numHoles : '18');

            if (record.tee) {
                const recordedTee = record.tee as iGolfCourseTee;
                setSelectedTee(recordedTee);
                setiGolfTeeText(getTeeDetailsText(recordedTee));
            }
        }
    }, [record]);

    useEffect(function loadAllStates() {
        getStateListByCountries();
        const localGender = localStorage.getItem('iGolfGender');
        if (localGender) {
            setGender(localGender);
        }
    }, []);

    useEffect(function setSelectedTeeTextOnShow() {
        if (show) {
            if (selectedTee) {
                setiGolfTeeText(getTeeDetailsText(selectedTee));
            }
        }
    }, [show]);

    useEffect(function loadiGolfTeeDetails(){
        if (selectedCourse) {
            getTeeDetails(selectedCourse.id_course);
        }
    }, [selectedCourse]);

    useEffect(function setSelectedTeeItemInMenuList() {
        if (teeDetails.length > 0 && record && selectedTee) {
            setiGolfTeeText(getTeeDetailsText(selectedTee));
        }
    }, [teeDetails])

    const valuesPopulated = () => {
        return !!(score && courseName && selectedTee && dateValue?.isValid())
    }

    const handleTeeSelectChange = (event: SelectChangeEvent) => {
        const selectedValue = event.target.value;
        setiGolfTeeText(selectedValue);
    }

    useEffect(function saveLastGender() {
        localStorage.setItem("iGolfGender", gender);
    }, [gender]);

    const resetForm = () => {
        setScore("");
        setDateValue(dayjs(new Date()));
        setCourseName("");
        setUpAndDowns("");
        setChippings("");
        setNumberOfPutts("");
        setMental("");
        setStrokes("");
        setHoles("");
        setFairway("");
        setGreensInRegulation("");
        resetCourseField();
        setCourseList([]);
        setTeeDetails([]);
        setSelectedTee(null);
        setiGolfTeeText('');
        setNumHoles('18');
    }

    const submitScoreCard = () => {
        const requestData: recordScore = {
            score: Number(score),
            record_date: dateValue ? dateValue.format("YYYY-MM-DD").toString() : "",
            course: courseName,
            chipping_bunkers_1: upAndDowns,
            chipping_bunkers_2: chippings,
            putting: numberOfPutts,
            mental_game: mental,
            wedge_game_1: strokes,
            wedge_game_2: holes,
            driving: fairway,
            ball_striking: greensInRegulation,
            record_id: Number(recordID),
            tee: selectedTee,
            gender: gender,
            courseObj: selectedCourse,
            numHoles: numHoles
        }
        const statsIncluded = extractActivePropertyLabels(requestData);
        scorecardAddEvent(statsIncluded)

        setLoading(true);
        axiosRequestData("POST", "/wp-json/pg/v2/sc/score-tracker", requestData)
            .then(response => {
                setShow(false);
                resetForm();
                if (callback)
                    callback(response.data.data, requestData);
            })
            .finally(() => setLoading(false))
    }

    const handleClose = () => {
        setShow(false);
    }

    const handleTeeChange = (tee: iGolfCourseTee) => {
        setSelectedTee(tee);
    };

    const setHolesByScore = (e: any) => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            const scoreValue = parseInt(e.target.value);
            if (scoreValue < 60) {
                setNumHoles('front9');
            }
        }, 750);
    };

    const resetCourseField = () => {
        setSelectedCourse(null);
    }

    return (
        <Dialog
            fullWidth
            fullScreen={isMobile}
            maxWidth={"md"}
            open={show}
            onClose={handleClose}
            PaperProps={{sx: {borderRadius: isMobile ? 0 : "10px"}}}
        >
            <DialogHeader title={isMobile ? <Box component="img" margin="auto" display="flex"
                                                 src="https://www.performancegolf.com/wp-content/uploads/2023/11/Vector-1.png"
                                                 alt="logo"/> : "Submit Scoreboard"}
                          closeAction={handleClose}></DialogHeader>

            <DialogContent>
                <ScoreCardForm
                    dateValue={dateValue}
                    setDateValue={setDateValue}
                    score={score}
                    setScore={setScore}
                    courseName={courseName}
                    setCourseName={setCourseName}
                    fairway={fairway}
                    setFairway={setFairway}
                    greensInRegulation={greensInRegulation}
                    setGreensInRegulation={setGreensInRegulation}
                    numberOfPutts={numberOfPutts}
                    setNumberOfPutts={setNumberOfPutts}
                    upAndDowns={upAndDowns}
                    setUpAndDowns={setUpAndDowns}
                    chippings={chippings}
                    setChippings={setChippings}
                    mental={mental}
                    setMental={setMental}
                    strokes={strokes}
                    setStrokes={setStrokes}
                    holes={holes}
                    setHoles={setHoles}
                    expanded={expanded}
                    setExpanded={setExpanded}
                    numHoles={numHoles}
                    setNumHoles={setNumHoles}
                    iGolfTeeText={iGolfTeeText}
                    setiGolfTeeText={setiGolfTeeText}
                    selectedCourse={selectedCourse}
                    setSelectedCourse={setSelectedCourse}
                    selectedTee={selectedTee}
                    setSelectedTee={setSelectedTee}
                    gender={gender}
                    setGender={setGender}
                    courseList={courseList}
                    searchiGolfCourses={searchiGolfCourses}
                    countryStateList={countryStateList}
                    teeDetails={teeDetails}
                    loadingiGolf={loadingiGolf}
                    loadingTee={loadingTee}
                    getTeeDetailsText={getTeeDetailsText}
                    handleTeeSelectChange={handleTeeSelectChange}
                    handleTeeChange={handleTeeChange}
                    setHolesByScore={setHolesByScore}
                    valuesPopulated={valuesPopulated}
                    submitScoreCard={submitScoreCard}
                    loading={loading}
                    onCancel={handleClose}
                    record={record}
                    showDialog={show}
                    isInDialog={true}
                />
            </DialogContent>
        </Dialog>
    );
};


export default ScoreCardDialog;

